import BasicButton from "@/components/core/buttons/BasicButton";
import { ROUTES } from "@/routes/Routes";
import { degradedStore } from "@/state/degraded/DegradedStore";
import { DegradedModeDTO } from "@/state/degraded/dto/response/degraded.mode.dto";
import { Signatory } from "@/type/core/dashboard/dashboard.types";
import { Right } from "@/type/core/offices/offices.types";
import { TransactionDetail } from "@/type/core/transaction/transaction.types";
import DateHelper from "@/utils/helpers/date-helper";
import { TransactionHelper } from "@/utils/helpers/transaction-helper";
import TransactionClassifyButton from "@/views/core/extranet/transactions/details/buttons/TransactionClassifyButton";
import TransactionISignButton from "@/views/core/extranet/transactions/details/buttons/TransactionISignButton";
import { useUnit } from "effector-react";
import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import TransactionLaunchButton from "@views/core/extranet/transactions/details/buttons/TransactionLaunchButton";
import { handleRelaunchSignatory } from "@/utils/helpers/signatories-helper";
import ConfirmModal from "@/components/core/modals/ConfirmModal";

interface TransactionButtonsDashboardSectionProps {
  transaction: TransactionDetail;
  officeRights: Right[];
  refetchTransactions: () => void;
  setIsLoading: (isLoading: boolean) => void;
  userEmail?: string;
  userSerialNumber?: string;
  nextSignatory?: Signatory;
}

const TransactionButtonsDashboardSection: FunctionComponent<
  TransactionButtonsDashboardSectionProps
> = ({
  transaction,
  officeRights,
  refetchTransactions,
  setIsLoading,
  userEmail,
  userSerialNumber,
  nextSignatory,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const degradedModeStore = useUnit<DegradedModeDTO | null>(degradedStore);

  const transactionStatus = transaction.status;
  const classified = transaction.classified;
  const isTransactionExpiredAndEmittedSinceMoreThen60Days: undefined | boolean =
    transaction.emittedDate != undefined &&
    transactionStatus === "expired" &&
    DateHelper.isTodayAfterDatePlusDays(transaction.emittedDate, 60);
  const directSignature =
    nextSignatory &&
    nextSignatory.email === userEmail &&
    userSerialNumber &&
    officeRights.includes("SIGNATURE_COLLECTE_SIGNER");

  const [disabledAfterLaunch, setDisabledAfterLaunch] =
    useState<boolean>(false);
  const [isConfirmModalVisible, setIsConfirmModalVisible] =
    useState<boolean>(false);

  const launchTransactionCallback = (transactionId: string) => {
    navigate(ROUTES.extranet.transactions.process.generate(transactionId));
    setDisabledAfterLaunch(true);
  };

  const getProperDate = (date?: string) => {
    if (!date) {
      return;
    }
    const [dateData, timeData] = date.split(" ");

    const [, month, day] = dateData.split("-");
    const [hours, minutes] = timeData?.split(":");

    const prettyDate = `${day}/${month}`;
    const prettyTime = `${hours}h${minutes}`;

    return `le ${prettyDate} à ${prettyTime}`;
  };

  const transactionRelaunchButton = (relaunch: boolean, label: string) => (
    <TransactionLaunchButton
      label={label}
      transaction={transaction}
      signatories={transaction.signatories ?? []}
      officeRights={officeRights}
      signatureRight={"SIGNATURE_COLLECTE_LANCER"}
      scellementRight={"SCELLEMENT_COLLECTE_LANCER"}
      relaunch={relaunch}
      launchTransaction={(relaunch, direct) =>
        TransactionHelper.launchTransaction(
          transaction,
          launchTransactionCallback,
          relaunch,
          direct,
        )
      }
      disabled={false}
      disabledAfterLaunch={disabledAfterLaunch}
      setDisabledAfterLaunch={setDisabledAfterLaunch}
      // TODO show direct sign modal from the dasbhoard "Lancer" button ?
      setIsDirectSignatureModalOpen={() => null}
      certificateDegradedMode={
        degradedModeStore?.certificateDegradedMode ?? false
      }
      signatureDegradedMode={degradedModeStore?.signatureDegradedMode ?? false}
      sealDegradedMode={degradedModeStore?.sealDegradedMode ?? false}
      className="btn-transaction-dashboard w-100"
    />
  );

  const signatoryRelaunchButton = (signatory: Signatory) => {
    return (
      <>
        <BasicButton
          variant="primary"
          text={t("home.dashboard.table.buttons.signatoryRelaunch")}
          onClick={() => setIsConfirmModalVisible(true)}
          className="py-11 px-30 font-extra-small btn-transaction-dashboard w-100"
        />
        <ConfirmModal
          visible={isConfirmModalVisible}
          onCancel={() => {
            setIsConfirmModalVisible(false);
          }}
          onOk={() => {
            setIsConfirmModalVisible(false);
            void handleRelaunchSignatory({
              signatureDegradedMode:
                degradedModeStore?.signatureDegradedMode ?? false,
              signatoryEmail: signatory.email,
              signatoryId: signatory.id,
              transactionId: transaction.id,
              transactionInvitationMode: transaction.invitationMode,
              setIsLoading,
              callbackOnSuccess: refetchTransactions,
            });
          }}
          title={t("modals.relaunchSignatoryConfirmationModal.title")}
          content={t("modals.relaunchSignatoryConfirmationModal.content", {
            date: nextSignatory?.relaunchDate
              ? getProperDate(nextSignatory?.relaunchDate)
              : getProperDate(nextSignatory?.invitationDate),
            action: nextSignatory?.relaunchDate ? "relancé" : "invité",
          })}
        />
      </>
    );
  };

  switch (transactionStatus) {
    // Bouton "Je signe"
    case "inProgress":
      if (degradedModeStore?.signatureDegradedMode) {
        return null;
      }
      if (directSignature) {
        return (
          <TransactionISignButton
            nextSignatory={nextSignatory}
            userEmail={userEmail}
            userSerialNumber={userSerialNumber}
            officeRights={officeRights}
            className="btn-transaction-dashboard w-100"
            transactionInvitationMode={transaction.invitationMode}
          />
        );
      } else if (
        transaction.notification &&
        transaction.notification === "DISABLED"
      ) {
        return null;
      } else if (nextSignatory) {
        return signatoryRelaunchButton(nextSignatory);
      } else {
        return null;
      }
    // Bouton "Classer"
    case "refused":
    case "finished":
    case "canceled":
      if (!classified) {
        return (
          <TransactionClassifyButton
            transaction={transaction}
            onClick={(status, cause) =>
              TransactionHelper.updateTransaction(
                transaction.id,
                transaction,
                status,
                cause,
                setIsLoading,
                refetchTransactions,
              )
            }
            officeRights={officeRights}
            signatureRight={"SIGNATURE_COLLECTE_CLASSER"}
            scellementRight={"SCELLEMENT_COLLECTE_CLASSER"}
            label={t("home.dashboard.table.buttons.classify")}
            className="btn-transaction-dashboard w-100"
          />
        );
      }
      return null;
    case "expired":
      if (
        transaction.hasData &&
        !classified &&
        transaction.universignVersion === "U1" &&
        officeRights.includes("SIGNATURE_COLLECTE_LANCER") &&
        !isTransactionExpiredAndEmittedSinceMoreThen60Days
      ) {
        return transactionRelaunchButton(
          true,
          t("home.dashboard.table.buttons.transactionRelaunch"),
        );
      } else {
        return (
          <TransactionClassifyButton
            transaction={transaction}
            onClick={(status, cause) =>
              TransactionHelper.updateTransaction(
                transaction.id,
                transaction,
                status,
                cause,
                setIsLoading,
                refetchTransactions,
              )
            }
            officeRights={officeRights}
            signatureRight={"SIGNATURE_COLLECTE_CLASSER"}
            scellementRight={"SCELLEMENT_COLLECTE_CLASSER"}
            label={t("home.dashboard.table.buttons.classify")}
            className="btn-transaction-dashboard w-100"
          />
        );
      }
    case "prepared":
      if (officeRights.includes("SIGNATURE_COLLECTE_LANCER")) {
        return transactionRelaunchButton(false, t("buttons.send"));
      } else {
        return null;
      }
    default:
      return null;
  }
};

export default TransactionButtonsDashboardSection;
